import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { So5LeaderboardSeasonality } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Boost } from '@sorare/core/src/atoms/icons/Boost';
import { Lightning } from '@sorare/core/src/atoms/icons/Lightning';
import { CenterVertically } from '@sorare/core/src/atoms/layout/CenterVertically';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { HeadlineL, LabelS, LabelXS } from '@sorare/core/src/atoms/typography';
import { SplitProgressBar } from '@sorare/core/src/atoms/ui/SplitProgressBar';
import { ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { range } from '@sorare/core/src/lib/arrays';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { fantasy, glossary } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { BoostButton } from '@sorare/marketplace/src/components/card/BoostButton';
import { useIsBoostAffordable } from '@sorare/marketplace/src/hooks/useIsBoostAffordable';
import { useLevelUpCards } from '@sorare/marketplace/src/hooks/useLevelUpCards';

import { CollectionHexagon } from '#src/components/lineup/LineupBonusDetails/CollectionHexagon/index.tsx';
import { SeasonHexagon } from '#src/components/lineup/LineupBonusDetails/SeasonHexagon/index.tsx';
import { XPHexagon } from '#src/components/lineup/LineupBonusDetails/XPHexagon/index.tsx';
import { SeasonalityLabel } from '#src/components/lineup/SeasonalityLabel/index.tsx';

import { BreakdownBlock } from './BreakdownBlock/index.tsx';
import { CardBlock } from './CardBlock/index.tsx';
import type { AppearanceBonusBreakdown_appearance } from './__generated__/index.graphql.ts';
import { messages } from './messages.ts';

const Content = styled(Vertical)`
  padding: var(--double-unit) 0;
`;
const BreakdownWrapper = styled(Vertical).attrs({ gap: 3 })`
  position: relative;
  padding-top: var(--triple-unit);
  isolation: isolate;
`;
const LinesSvg = styled.svg`
  width: 100%;
  position: absolute;
  top: 0;
  z-index: -1;
`;
const Blocks = styled.div`
  display: flex;

  > * {
    flex: 1;
  }

  gap: var(--quadruple-unit);
  align-items: start;
`;
const BrandHeaderlineL = styled(HeadlineL)`
  background: var(--c-black);
  align-self: center;
  padding: var(--half-unit) var(--unit);

  font-family: var(--brand-font);
  text-shadow: 0px 0px 8px rgba(255, 255, 255, 0.58);
`;
const BrandLabelS = styled(LabelS)`
  font-family: var(--brand-font);
`;
const BrandLabelXS = styled(LabelXS)`
  font-family: var(--brand-font);
`;

type Props = {
  appearance: AppearanceBonusBreakdown_appearance;
  cardMaxGrade: number;
  seasonalityName: string;
};

export const AppearanceBonusBreakdown = ({
  appearance,
  cardMaxGrade,
  seasonalityName,
}: Props) => {
  const { generateSportContextPath } = useSportContext();
  const track = useEvents();

  const [confirmBoost, setConfirmBoost] = useState(false);
  const { levelUpCards, loading: mutationLoading } = useLevelUpCards();
  const isBoostAffordable = useIsBoostAffordable();

  const { anyCard, powerBreakdownOrPowerPointsBreakdown, powerBreakdown } =
    appearance;
  if (!powerBreakdownOrPowerPointsBreakdown) {
    return null;
  }

  const { xpNeeded = 0 } = anyCard ? isBoostAffordable(anyCard) : {};

  const cardCollectionCard =
    appearance?.anyCard && isType(appearance.anyCard, 'Card')
      ? appearance.anyCard.cardCollectionCards.at(0)
      : null;

  const { cardCollection, userCardCollection } = cardCollectionCard || {};

  const reachedMaxCollectionBonus = cardCollection?.bonusThresholds.every(
    ({ threshold }) => (userCardCollection?.score || 0) > threshold
  );

  const isMaxLevel = anyCard?.xpNeededForNextGrade === null;

  const onBoostClick = () => {
    if (!anyCard) {
      return;
    }
    if (!confirmBoost) {
      setConfirmBoost(true);
      return;
    }
    track('Click Level Up Cards', {
      cardSlugs: [anyCard.slug],
      rarity: anyCard.rarity,
    });
    levelUpCards({
      levelUpCards: [
        {
          cardSlug: anyCard.slug,
          xpNeeded,
        },
      ],
    });
  };

  return (
    <CenterVertically>
      <Content center>
        <CardBlock appearance={appearance} />
        <BreakdownWrapper>
          <LinesSvg
            xmlns="http://www.w3.org/2000/svg"
            width="246"
            height="75"
            viewBox="0 0 246 75"
            fill="none"
          >
            <path d="M1 75V40H121" stroke="white" strokeOpacity="0.17" />
            <path d="M123 75V0" stroke="white" strokeOpacity="0.17" />
            <path d="M245 75V40H125" stroke="white" strokeOpacity="0.17" />
          </LinesSvg>

          <BrandHeaderlineL>
            <Horizontal center gap={0.5}>
              {isType(
                powerBreakdownOrPowerPointsBreakdown,
                'PowerBreakdown'
              ) ? (
                <FormattedMessage
                  {...messages.numberPercent}
                  values={{
                    number: (
                      <FormattedNumber
                        value={
                          (powerBreakdownOrPowerPointsBreakdown.collectionBasisPoints +
                            powerBreakdownOrPowerPointsBreakdown.seasonBasisPoints +
                            powerBreakdownOrPowerPointsBreakdown.xpBasisPoints) /
                          100
                        }
                        maximumFractionDigits={1}
                        signDisplay="always"
                      />
                    ),
                    span: (...chunks: ReactNode[]) => (
                      <BrandLabelS bold color="var(--c-nd-600)">
                        {chunks}
                      </BrandLabelS>
                    ),
                  }}
                />
              ) : (
                <FormattedMessage
                  {...fantasy.ordinalPoints}
                  values={{
                    score: (
                      <FormattedNumber
                        value={
                          powerBreakdownOrPowerPointsBreakdown.xpPoints +
                          powerBreakdownOrPowerPointsBreakdown.collectionPoints +
                          powerBreakdownOrPowerPointsBreakdown.seasonPoints
                        }
                        maximumFractionDigits={1}
                        signDisplay="always"
                      />
                    ),
                    sup: (...chunks: ReactNode[]) => (
                      <BrandLabelXS bold uppercase color="var(--c-nd-600)">
                        {chunks}
                      </BrandLabelXS>
                    ),
                  }}
                />
              )}
            </Horizontal>
          </BrandHeaderlineL>
          <Blocks>
            <BreakdownBlock
              border={isMaxLevel}
              percentBasisPoints={
                isType(powerBreakdownOrPowerPointsBreakdown, 'PowerBreakdown')
                  ? powerBreakdownOrPowerPointsBreakdown.xpBasisPoints
                  : null
              }
              points={
                isType(
                  powerBreakdownOrPowerPointsBreakdown,
                  'PowerPointsBreakdown'
                )
                  ? powerBreakdownOrPowerPointsBreakdown.xpPoints
                  : null
              }
              Icon={XPHexagon}
              label={
                <Horizontal gap={0.5}>
                  <Boost color="var(--c-rewards-blue)" />
                  <LabelS bold color="var(--c-rewards-blue)">
                    <FormattedMessage defaultMessage="Card Lvl" />
                  </LabelS>
                </Horizontal>
              }
              bar={
                <SplitProgressBar
                  thresholds={range(cardMaxGrade).map(i => ({ score: i + 1 }))}
                  currentScore={anyCard?.grade || 0}
                  color="var(--c-rewards-blue)"
                />
              }
              cta={
                !!anyCard &&
                isType(
                  powerBreakdownOrPowerPointsBreakdown,
                  'PowerBreakdown'
                ) && (
                  <BoostButton
                    size="compact"
                    anyCard={anyCard}
                    needConfirm={confirmBoost}
                    loading={mutationLoading}
                    onClick={onBoostClick}
                  />
                )
              }
            />
            <BreakdownBlock
              border={!!reachedMaxCollectionBonus}
              percentBasisPoints={
                isType(powerBreakdownOrPowerPointsBreakdown, 'PowerBreakdown')
                  ? powerBreakdownOrPowerPointsBreakdown.collectionBasisPoints
                  : null
              }
              points={
                isType(
                  powerBreakdownOrPowerPointsBreakdown,
                  'PowerPointsBreakdown'
                )
                  ? powerBreakdownOrPowerPointsBreakdown.collectionPoints
                  : null
              }
              Icon={CollectionHexagon}
              label={
                <Horizontal gap={0.5}>
                  <Lightning color="var(--c-yellow-300)" />
                  <LabelS bold color="var(--c-yellow-300)">
                    <FormattedMessage defaultMessage="Collection" />
                  </LabelS>
                </Horizontal>
              }
              bar={
                <SplitProgressBar
                  thresholds={
                    cardCollection?.bonusThresholds.map(({ threshold }) => ({
                      score: threshold,
                    })) || []
                  }
                  currentScore={userCardCollection?.score || 0}
                  color="var(--c-yellow-400)"
                />
              }
              cta={
                !!cardCollection &&
                isType(
                  powerBreakdownOrPowerPointsBreakdown,
                  'PowerBreakdown'
                ) && (
                  <Button
                    fullWidth
                    size="compact"
                    color="tertiary"
                    startIcon={<Lightning color="var(--c-yellow-400)" />}
                    to={
                      !reachedMaxCollectionBonus
                        ? generateSportContextPath(
                            ANY_SPORT_MY_CARDS_COLLECTION_COLLECTIONSLUG,
                            {
                              params: { collectionSlug: cardCollection.slug },
                            }
                          )
                        : undefined
                    }
                    disabled={!!reachedMaxCollectionBonus}
                  >
                    {reachedMaxCollectionBonus ? (
                      <FormattedMessage {...glossary.max} />
                    ) : (
                      <FormattedMessage defaultMessage="View" />
                    )}
                  </Button>
                )
              }
            />
            <BreakdownBlock
              border={powerBreakdown.seasonBasisPoints > 0}
              percentBasisPoints={
                isType(powerBreakdownOrPowerPointsBreakdown, 'PowerBreakdown')
                  ? powerBreakdownOrPowerPointsBreakdown.seasonBasisPoints
                  : null
              }
              points={
                isType(
                  powerBreakdownOrPowerPointsBreakdown,
                  'PowerPointsBreakdown'
                )
                  ? powerBreakdownOrPowerPointsBreakdown.seasonPoints
                  : null
              }
              Icon={SeasonHexagon}
              label={
                <LabelS bold>
                  <SeasonalityLabel
                    seasonality={So5LeaderboardSeasonality.IN_SEASON}
                    label={seasonalityName}
                  />
                </LabelS>
              }
              bar={
                <SplitProgressBar
                  thresholds={range(5).map(i => ({ score: i + 1 }))}
                  currentScore={powerBreakdown.seasonBasisPoints > 0 ? 5 : 0}
                  color="iridescent"
                />
              }
            />
          </Blocks>
        </BreakdownWrapper>
      </Content>
    </CenterVertically>
  );
};

AppearanceBonusBreakdown.fragments = {
  appearance: gql`
    fragment AppearanceBonusBreakdown_appearance on AppearanceInterface {
      id
      powerBreakdown {
        seasonBasisPoints
      }
      powerBreakdownOrPowerPointsBreakdown {
        ... on PowerBreakdown {
          collectionBasisPoints
          seasonBasisPoints
          xpBasisPoints
        }
        ... on PowerPointsBreakdown {
          collectionPoints
          seasonPoints
          xpPoints
        }
      }
      anyCard {
        slug
        rarity: rarityTyped
        grade
        xpNeededForNextGrade
        cardCollectionCards {
          id
          userCardCollection {
            id
            score
          }
          cardCollection {
            slug
            bonusThresholds {
              bonus
              threshold
            }
          }
        }
        ...BoostButton_anyCard
        ...useIsBoostAffordable_anyCard
      }
      ...CardBlock_appearance
    }
    ${CardBlock.fragments.appearance}
    ${BoostButton.fragments.anyCard}
    ${useIsBoostAffordable.fragments.anyCard}
  ` as TypedDocumentNode<AppearanceBonusBreakdown_appearance>,
};
